/**
 * Threshold Timeline Projection (Phase 2 — Step 2.13).
 *
 * Projects when the next penalty tier will trigger based on current miss rate.
 */

import React, { useMemo } from 'react';
import { computeTotalSessions, getPastSessionCount } from '@abs/shared-logic';
import { computeEffectiveAbsences } from '@abs/shared-logic';
import { resolveActiveTier } from '@abs/shared-logic';
import type { AbsenceEntry, AttendancePolicy, Course } from '@abs/shared-types';

interface ThresholdTimelineProps {
  course: Course;
  policy: AttendancePolicy;
  entries: AbsenceEntry[];
}

export function ThresholdTimeline({ course, policy, entries }: ThresholdTimelineProps): React.ReactElement {
  const projection = useMemo(() => {
    const { total, sessions } = computeTotalSessions(
      course.meetingPatterns,
      course.sessionOverrides,
      course.startDate,
      course.endDate,
    );
    const pastCount = getPastSessionCount(sessions);
    const remaining = total - pastCount;

    const effective = computeEffectiveAbsences(entries, policy);
    const { nextTier } = resolveActiveTier(policy.tiers, effective.totalPenaltyAbsences);

    if (!nextTier) {
      return {
        nextTierDate: null,
        nextTierDescription: null,
        absencesNeeded: 0,
        missRate: 0,
        projectedDate: null,
      };
    }

    // Current miss rate
    const missRate = pastCount > 0 ? effective.totalPenaltyAbsences / pastCount : 0;

    // How many more absences to reach next tier
    const absencesNeeded = nextTier.absenceThreshold - effective.totalPenaltyAbsences;

    // Projected sessions until next tier (based on miss rate)
    const sessionsUntilNext = missRate > 0
      ? Math.ceil(absencesNeeded / missRate)
      : Infinity;

    // Projected date
    let projectedDate: string | null = null;
    if (sessionsUntilNext !== Infinity && sessionsUntilNext <= remaining) {
      const futureSessions = sessions.slice(pastCount);
      if (futureSessions[sessionsUntilNext - 1]) {
        projectedDate = futureSessions[sessionsUntilNext - 1].date;
      }
    }

    return {
      nextTierDate: projectedDate,
      nextTierDescription: nextTier.description,
      absencesNeeded,
      missRate,
      projectedDate,
    };
  }, [course, policy, entries]);

  if (!projection.nextTierDescription) {
    return (
      <div className="threshold-timeline" role="status">
        <p className="threshold-timeline--safe">
          You are below all penalty thresholds. Keep up the good attendance!
        </p>
      </div>
    );
  }

  return (
    <div className="threshold-timeline" role="region" aria-label="Threshold projection">
      <div className="threshold-timeline__info">
        <p>
          <strong>Next tier:</strong> {projection.nextTierDescription}
        </p>
        <p>
          <strong>Absences needed:</strong> {projection.absencesNeeded}
        </p>
        <p>
          <strong>Current miss rate:</strong> {(projection.missRate * 100).toFixed(1)}%
          (1 absence every {projection.missRate > 0 ? Math.round(1 / projection.missRate) : '∞'} sessions)
        </p>
        {projection.projectedDate ? (
          <p className="threshold-timeline__date">
            At current pace, you&apos;ll hit this tier on <strong>{projection.projectedDate}</strong>
          </p>
        ) : (
          <p className="threshold-timeline--safe">
            At current pace, you won&apos;t reach this tier.
          </p>
        )}
      </div>
    </div>
  );
}
